import { useState, useMemo } from 'react'
import {
  FileText,
  Download,
  X,
  Eye,
  Image as ImageIcon,
  Video,
  Music,
  FileIcon,
  Grid3x3,
  List,
  Play,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { motion, AnimatePresence } from 'framer-motion'
import { buildBackendUrl } from '@/lib/backend-url'

// Helper function to get file extension
const getFileExtension = (filename) => {
  if (!filename || typeof filename !== 'string') return ''
  const parts = filename.split('.')
  return parts.length > 1 ? parts.pop().toLowerCase() : ''
}

// Helper function to determine file type
const getFileType = (filename) => {
  if (!filename) return 'other'
  const ext = getFileExtension(filename)

  const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico']
  const videoExts = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv']
  const audioExts = ['mp3', 'wav', 'ogg', 'aac', 'm4a', 'flac']
  const pdfExts = ['pdf']

  if (imageExts.includes(ext)) return 'image'
  if (videoExts.includes(ext)) return 'video'
  if (audioExts.includes(ext)) return 'audio'
  if (pdfExts.includes(ext)) return 'pdf'
  return 'other'
}

// Helper function to get file icon
const getFileIcon = (filename) => {
  const type = getFileType(filename)

  switch (type) {
    case 'image':
      return ImageIcon
    case 'video':
      return Video
    case 'audio':
      return Music
    case 'pdf':
      return FileText
    default:
      return FileIcon
  }
}

// Get a preview URL from backend
const getFileUrl = (attachment) => {
  if (!attachment?.id) return ''
  return buildBackendUrl(`/api/attachments/${attachment.id}/download`)
}

const FilePreviewModal = ({ isOpen, onClose, attachment }) => {
  const filename = attachment?.file_name || ''
  const type = getFileType(filename)
  const url = useMemo(() => getFileUrl(attachment), [attachment])

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[90vw] md:max-w-4xl max-h-[90vh] p-0 overflow-hidden border-none shadow-2xl bg-background/95 backdrop-blur-xl">
        <DialogHeader className="p-4 border-b border-border/10 bg-muted/30 flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-sm font-semibold truncate flex items-center gap-2">
            {filename}
          </DialogTitle>
          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </DialogHeader>

        <div className="relative flex items-center justify-center min-h-[400px] bg-black/5 p-6">
          {type === 'image' && (
            <motion.img
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              src={url}
              alt={filename}
              className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-xl"
            />
          )}

          {type === 'video' && (
            <div className="w-full h-full max-w-3xl overflow-hidden rounded-xl shadow-2xl bg-black">
              <video src={url} controls autoPlay className="w-full h-full" />
            </div>
          )}

          {type === 'audio' && (
            <div className="flex flex-col items-center gap-6 p-12 bg-card rounded-2xl shadow-xl border border-border/10 w-full max-w-md">
              <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
                <Music className="h-10 w-10 text-primary" />
              </div>
              <div className="text-center">
                <h3 className="font-semibold">{filename}</h3>
                <p className="text-sm text-muted-foreground mt-1">Audio File</p>
              </div>
              <audio src={url} controls className="w-full" />
            </div>
          )}

          {type === 'pdf' && (
            <iframe
              src={url}
              className="w-full h-[70vh] rounded-lg border border-border/10 shadow-inner"
              title={filename}
            />
          )}

          {type === 'other' && (
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="w-20 h-20 rounded-2xl bg-muted flex items-center justify-center">
                <FileIcon className="h-10 w-10 text-muted-foreground" />
              </div>
              <div>
                <p className="font-medium">{filename}</p>
                <p className="text-sm text-muted-foreground mt-1 uppercase">
                  {getFileExtension(filename)} file
                </p>
              </div>
              <Button onClick={() => window.open(url, '_blank')} className="mt-2">
                Open in new tab
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

const AttachmentCard = ({ attachment, onRemove, onDownload, onPreview }) => {
  const filename = attachment?.file_name || ''
  const type = getFileType(filename)
  const url = useMemo(() => getFileUrl(attachment), [attachment])
  const Icon = getFileIcon(filename)

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="group relative flex flex-col rounded-2xl border border-border/10 bg-card/50 shadow-sm transition-all hover:bg-card hover:shadow-md hover:border-primary/20 overflow-hidden"
    >
      <div
        className="relative aspect-[4/3] w-full overflow-hidden bg-muted/30 cursor-pointer"
        onClick={() => onPreview(attachment)}
      >
        {type === 'image' && (
          <img
            src={url}
            alt={filename}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
        )}

        {type === 'video' && (
          <div className="relative h-full w-full flex items-center justify-center bg-black/5">
            <video src={url} className="h-full w-full object-cover opacity-80" muted />
            <div className="absolute inset-0 flex items-center justify-center transition-transform group-hover:scale-110">
              <div className="rounded-full bg-primary p-3 shadow-lg">
                <Play className="h-5 w-5 text-primary-foreground fill-current" />
              </div>
            </div>
          </div>
        )}

        {type === 'audio' && (
          <div className="flex h-full w-full flex-col items-center justify-center gap-3 bg-gradient-to-br from-primary/5 to-primary/10">
            <div className="rounded-full bg-primary/20 p-3">
              <Music className="h-6 w-6 text-primary" />
            </div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-primary/60">
              Audio Preview
            </span>
          </div>
        )}

        {type === 'pdf' && (
          <div className="flex h-full w-full flex-col items-center justify-center gap-3 bg-gradient-to-br from-red-500/5 to-red-500/10">
            <div className="rounded-full bg-red-500/20 p-3">
              <FileText className="h-6 w-6 text-red-500" />
            </div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-red-500/60">
              PDF Document
            </span>
          </div>
        )}

        {type === 'other' && (
          <div className="flex h-full w-full flex-col items-center justify-center gap-3 bg-muted/20">
            <div className="rounded-full bg-muted-foreground/10 p-3">
              <Icon className="h-6 w-6 text-muted-foreground" />
            </div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/60">
              {getFileExtension(filename)} File
            </span>
          </div>
        )}

        {/* Action Overlay */}
        <div className="absolute inset-x-0 bottom-0 flex scale-95 translate-y-2 justify-center gap-1.5 p-3 opacity-0 transition-all group-hover:scale-100 group-hover:translate-y-0 group-hover:opacity-100">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="secondary"
                  className="h-8 w-8 rounded-full shadow-lg backdrop-blur-md"
                  onClick={(e) => {
                    e.stopPropagation()
                    onPreview(attachment)
                  }}
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Preview</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="secondary"
                  className="h-8 w-8 rounded-full shadow-lg backdrop-blur-md"
                  onClick={(e) => {
                    e.stopPropagation()
                    onDownload(attachment.id, filename)
                  }}
                >
                  <Download className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Download</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        <Button
          size="icon"
          variant="ghost"
          className="absolute right-2 top-2 h-7 w-7 rounded-full bg-background/50 opacity-0 transition-opacity hover:bg-destructive hover:text-destructive-foreground group-hover:opacity-100"
          onClick={(e) => {
            e.stopPropagation()
            onRemove(e)
          }}
          data-id={attachment.id}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="p-3">
        <p className="truncate text-xs font-semibold text-foreground/80">{filename}</p>
      </div>
    </motion.div>
  )
}

const AttachmentListItem = ({ attachment, onRemove, onDownload, onPreview }) => {
  const filename = attachment?.file_name || ''
  const Icon = getFileIcon(filename)

  return (
    <motion.div
      layout
      className="group flex items-center gap-3 rounded-xl border border-transparent bg-muted/20 px-3 py-2 transition-all hover:bg-muted/40 hover:border-border/10"
    >
      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-background shadow-sm border border-border/20">
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>

      <span className="min-w-0 flex-1 truncate text-xs font-medium text-foreground/80">
        {filename}
      </span>

      <div className="flex items-center gap-1 opacity-100 md:opacity-0 transition-opacity group-hover:opacity-100">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-lg hover:bg-primary/10 hover:text-primary"
                onClick={() => onPreview(attachment)}
              >
                <Eye className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Preview</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-lg hover:bg-primary/10 hover:text-primary"
                onClick={() => onDownload(attachment.id, filename)}
              >
                <Download className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Download</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-lg hover:bg-destructive/10 hover:text-destructive"
                onClick={onRemove}
                data-id={attachment.id}
              >
                <X className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Remove</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </motion.div>
  )
}

export const AttachmentViewer = ({ attachmentEntries, onRemove, onDownload }) => {
  const [viewMode, setViewMode] = useState('card')
  const [previewFile, setPreviewFile] = useState(null)

  return (
    <div className="space-y-4">
      {attachmentEntries.length > 0 && (
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground/60">
            {attachmentEntries.length}{' '}
            {attachmentEntries.length === 1 ? 'Attachment' : 'Attachments'}
          </span>
          <div className="flex items-center rounded-xl bg-muted/50 p-1 border border-border/10">
            <Button
              size="sm"
              variant={viewMode === 'card' ? 'secondary' : 'ghost'}
              className={cn(
                'h-7 px-2.5 rounded-lg text-xs font-semibold transition-all',
                viewMode === 'card' && 'bg-background shadow-sm',
              )}
              onClick={() => setViewMode('card')}
            >
              <Grid3x3 className="h-3.5 w-3.5 mr-1.5" />
              Cards
            </Button>
            <Button
              size="sm"
              variant={viewMode === 'list' ? 'secondary' : 'ghost'}
              className={cn(
                'h-7 px-2.5 rounded-lg text-xs font-semibold transition-all',
                viewMode === 'list' && 'bg-background shadow-sm',
              )}
              onClick={() => setViewMode('list')}
            >
              <List className="h-3.5 w-3.5 mr-1.5" />
              List
            </Button>
          </div>
        </div>
      )}

      <AnimatePresence mode="popLayout">
        {attachmentEntries.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center justify-center py-8 text-muted-foreground/30 border-2 border-dashed border-border/30 rounded-2xl"
          >
            <FileIcon className="h-10 w-10 mb-2 opacity-20" />
            <p className="text-xs font-medium">No files attached yet</p>
          </motion.div>
        ) : viewMode === 'card' ? (
          <motion.div
            key="card-view"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="grid grid-cols-1 gap-4"
          >
            {attachmentEntries.map((entry) => (
              <AttachmentCard
                key={entry.value.id}
                attachment={entry.value}
                onRemove={onRemove}
                onDownload={onDownload}
                onPreview={setPreviewFile}
              />
            ))}
          </motion.div>
        ) : (
          <motion.div
            key="list-view"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-2"
          >
            {attachmentEntries.map((entry) => (
              <AttachmentListItem
                key={entry.value.id}
                attachment={entry.value}
                onRemove={onRemove}
                onDownload={onDownload}
                onPreview={setPreviewFile}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <FilePreviewModal
        isOpen={!!previewFile}
        onClose={() => setPreviewFile(null)}
        attachment={previewFile}
      />
    </div>
  )
}
